import { AssetGroup } from '@play-co/timestep-core';
import { View } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import DreamDriftMap from 'src/game/dreamDrift/components/DreamDriftMap';
import DreamDriftTitle from 'src/game/dreamDrift/components/DreamDriftTitle';
import type { PopupDreamDriftLostInitArgs } from 'src/game/dreamDrift/popups/PopupDreamDriftLost';
import { dreamDriftPlayPuzzle } from 'src/game/dreamDrift/dreamDrift.sequence';
import { dreamDriftTheme } from 'src/game/dreamDrift/dreamDrift.theme';
import type {
  DreamDriftMapOpenOptions,
  DreamDriftRewardPresentation,
} from 'src/game/dreamDrift/dreamDrift.types';
import { Match2Timer } from 'src/game/minigame/match2-timestep/components/Match2Timer';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import {
  animDuration,
  getScreenDimensions,
  waitForItPromise,
} from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import {
  type DreamDriftMapPresentation,
  dreamDriftCanSelectHero,
  dreamDriftCanStartInstance,
  dreamDriftGetCurrentEvent,
  dreamDriftGetFinalNode,
  dreamDriftGetCheckpoints,
  dreamDriftGetInstanceRemainingTime,
  dreamDriftGetMapPresentation,
  dreamDriftGetMapRemainingTime,
  dreamDriftGetPresentationInstance,
} from 'src/replicant/dreamDrift/dreamDrift.getters';
import { dreamDriftHeroes } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import StateObserver, { now, user } from 'src/StateObserver';

/** Full-screen popup presenting the Dream Drift map and progression path. */
export default class PopupDreamDriftMap extends PopupBasicLazy {
  /** Theme supplying the map assets and themed path resolution. */
  private readonly theme = dreamDriftTheme();

  /** Persistent group used to load the selected theme and shared title assets. */
  private readonly assetsGroup = new AssetGroup([
    ...this.theme.getAssets(),
    ...AssetGroup.constructURLs(['assets/ui/dreamDrift/common']),
    'assets/sounds/music/DreamDrift_map_BGM.mp3',
  ]);

  /** Reusable map component configured after its assets finish loading. */
  private readonly dreamDriftMap: DreamDriftMap;

  /** Shared countdown showing the remaining time for the active Dream Drift. */
  private readonly timer: Match2Timer;

  /** Bottom action shown while the current Dream Drift puzzle can be played. */
  private readonly playButton: ButtonScaleViewWithText;

  /** Transparent shield blocking every popup interaction during presentation. */
  private readonly inputBlocker: View;

  /** Whether a pending avatar transition is still being presented. */
  private isPresentingProgress = true;

  /** Whether the Play action is waiting for the puzzle transition to settle. */
  private isStartingPuzzle = false;

  /** Prepared source and destination consumed by the fade-in presentation. */
  private mapPresentation?: DreamDriftMapPresentation;

  /** Pending reward settlement shown before the prepared map transition. */
  private rewardPresentation?: Promise<
    DreamDriftRewardPresentation | undefined
  >;

  /**
   * Create the full-screen map viewport and its ordered image strip.
   *
   * @param opts Popup parent and close callback supplied by the popup registry.
   */
  constructor(opts: { superview: View; close: () => void }) {
    // Match the popup width to its parent and extend it across the full screen.
    const width = opts.superview.style.width;
    const { height } = getScreenDimensions();

    // Create a borderless popup so the map occupies the complete viewport.
    super({
      superview: opts.superview,
      width,
      height,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      close: opts.close,
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_map',
      },
      zIndex: 10000,
    });

    // Clip the scrollable strip to the full-screen popup bounds.
    this.box.updateOpts({ clip: true });

    // Create the empty map component before its loaded configuration is applied.
    this.dreamDriftMap = new DreamDriftMap({
      superview: this.box,
      width,
    });

    // Keep the localized title centered above the scrolling map content.
    new DreamDriftTitle({
      superview: this.box,
      x: width / 2,
      y: 65,
      scale: 0.9,
      centerOnOrigin: true,
      zIndex: 1,
    });

    // Center the shared countdown immediately below the localized title.
    this.timer = new Match2Timer({
      superview: this.box,
      x: width / 2,
      y: 145,
      centerOnOrigin: true,
      zIndex: 1,
    });

    // Let an ongoing run reach zero before falling back to the event window.
    this.timer.setRemainingTimeGetter((currentTime) => {
      const state = user();
      const instance = dreamDriftGetPresentationInstance(state, currentTime);
      const hasOngoingInstance = instance !== undefined && !instance.result;
      if (hasOngoingInstance) {
        return dreamDriftGetInstanceRemainingTime(state, currentTime);
      }
      return dreamDriftGetMapRemainingTime(state, currentTime);
    });

    // Stop scheduled timer updates when the popup hierarchy is removed.
    this.timer.addListener('ViewRemoved', () => this.timer.stopAutoUpdate());

    // Open the event instructions from the standard map information control.
    new ButtonScaleView({
      superview: this.box,
      ...uiConfig.buttons.info,
      x: 50,
      y: 60,
      zIndex: 2,
      tracking: {
        feature: Feature.DREAM_DRIFT._,
        $subFeature: Feature.DREAM_DRIFT.BUTTON_INFO,
      },
      onClick: async () => {
        // Confirm the information-button tap with the standard popup sound.
        sounds.sfx.play('ok');

        // Present the reusable Dream Drift instructions above the map.
        await openPopupPromise('popupDreamDriftInfo', {});
      },
    });

    // Keep the primary play action fixed above the bottom edge of the map.
    this.playButton = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      x: width / 2,
      y: height - 150,
      width: 300,
      height: 100,
      centerOnOrigin: true,
      font: 'Body',
      fontSize: 50,
      labelOffsetY: -2,
      localeText: () => i18n('match2.ui.play'),
      visible: false,
      zIndex: 2,
      onClick: async () => {
        // Confirm the Play tap before starting the puzzle flow.
        sounds.sfx.play('ok');

        // Hide the action immediately and keep timer updates from restoring it.
        this.isStartingPuzzle = true;
        this.updatePlayButtonVisibility();

        try {
          // Start or resume the Dream Drift puzzle through its shared sequence.
          await dreamDriftPlayPuzzle();
        } finally {
          // Restore visibility when startup returns before this popup is removed.
          this.isStartingPuzzle = false;
          this.updatePlayButtonVisibility();
        }
      },
      tracking: {
        feature: 'dream_drift',
        $subFeature: 'dream_drift_map_play',
      },
    });

    // Keep button visibility synchronized with event and level playability.
    this.timer.setOnUpdate(() => this.updatePlayButtonVisibility());

    // Reconcile expiry and replay the map story when the run reaches zero.
    this.timer.setOnTimerEnd(async () => {
      // Ignore expiry callbacks while another map transition is presenting.
      if (this.isPresentingProgress) return;

      await StateObserver.invoke.dreamDriftSeenAt();
      await this.playOpeningAnimation();
    });

    // Keep the standard close control fixed above the scrolling map.
    this.buttonClose.updateOpts({
      x: width - 50,
      y: 60,
      zIndex: 2,
    });

    // Block scrolling and every popup button until the map presentation settles.
    this.inputBlocker = new View({
      superview: this.box,
      infinite: true,
      canHandleEvents: false,
      blockEvents: true,
      zIndex: 10000,
    });
  }

  /**
   * Load the complete Dream Drift map asset directory before showing the popup.
   *
   * @returns Asset group containing the map image slices and progress nodes.
   */
  public getAssetsGroup(): AssetGroup {
    // Reuse this popup's selected theme asset group throughout its lifecycle.
    return this.assetsGroup;
  }

  /** Synchronize the Play button with map presentation and gameplay gates. */
  private updatePlayButtonVisibility(): void {
    // The player cannot start another puzzle during map or puzzle transitions.
    if (this.isPresentingProgress || this.isStartingPuzzle) {
      this.playButton.updateOpts({ visible: false });
      return;
    }

    // Read the latest player state and server-authoritative time together.
    const state = user();
    const currentTime = now();

    // Require a current event and either a new or resumed playable instance.
    const hasCurrentEvent = !!dreamDriftGetCurrentEvent(state, currentTime);
    const canStartInstance = dreamDriftCanStartInstance(state, currentTime);
    const canSelectHero = dreamDriftCanSelectHero(state, currentTime);
    const canPlay = hasCurrentEvent && (canStartInstance || canSelectHero);

    // Show the bottom action only while the next puzzle can be prepared.
    this.playButton.updateOpts({ visible: canPlay });
  }

  /**
   * Present any reward settlement supplied by the current map opening.
   *
   * @returns Whether the player was presented with a reward chest.
   */
  private async presentRewards(): Promise<boolean> {
    // Map openings without pending rewards continue directly into presentation.
    const rewardPresentationPromise = this.rewardPresentation;
    if (!rewardPresentationPromise) return false;

    // Consume this one-time settlement before waiting for its backend result.
    this.rewardPresentation = undefined;
    const rewardPresentation = await rewardPresentationPromise;
    if (!rewardPresentation) return false;

    // Show the claimed rewards over the already visible Dream Drift map.
    await openPopupPromise('popupChestRewards', {
      ...rewardPresentation,
      feature: Feature.DREAM_DRIFT._,
      subFeature: Feature.DREAM_DRIFT.POPUP_CHEST,
    });

    // The reward chest was presented and acknowledged successfully.
    return true;
  }

  /**
   * Explain an expired run or pending checkpoint rollback before map movement.
   *
   * @returns Promise resolved after the player acknowledges the transition notice.
   */
  private async presentTransitionNotice(): Promise<void> {
    // The prepared presentation identifies the authoritative checkpoint destination.
    const presentation = this.mapPresentation;
    if (!presentation) return;

    // Resolve the matching instance for terminal state and retained hero copy.
    const instance = dreamDriftGetPresentationInstance(user(), now());

    // Expiry receipts require a notice even when the pin has nowhere to move.
    const isExpiredTransition =
      presentation.pendingProgress !== undefined &&
      instance !== undefined &&
      instance.startedAt === presentation.startedAt &&
      instance.result === 'expired';

    // Ordinary setbacks require a visible move back to an earlier checkpoint.
    const movesBackward = presentation.toProgress < presentation.fromProgress;
    if (!isExpiredTransition && !movesBackward) return;

    // Resolve a supported retained hero when one remains available.
    const storedHero = instance?.hero;
    const hero = dreamDriftHeroes.find(
      (configuredHero) => configuredHero === storedHero,
    );

    // Default to checkpoint copy for an ongoing run that remains playable.
    let popupArgs: PopupDreamDriftLostInitArgs = {
      reason: 'setback',
      hero,
    };

    // Expired instances need final copy because their run must begin again.
    if (isExpiredTransition) popupArgs = { reason: 'expired', hero };

    // Keep the map visible while explaining where the player will return.
    await openPopupPromise('popupDreamDriftLost', popupArgs);

    // Popup promises resolve when closing starts, before the fade-out detaches.
    await waitForItPromise(animDuration * 2);
  }

  /**
   * Fade in the popup before playing its Dream Drift map presentation.
   *
   * @returns Promise resolved after the complete opening sequence settles.
   */
  public override async fadeIn(): Promise<void> {
    // Restore the interaction shield whenever this popup begins opening.
    this.isPresentingProgress = true;
    this.inputBlocker.show();

    // Let the shared popup animate its background and box first.
    await super.fadeIn();

    // Play the loaded map track throughout progression and reward presentations.
    sounds.music.play('dreamDriftMap');

    // Position the map at its source pin before the popup becomes visible.
    const presentation = this.mapPresentation;
    if (presentation) {
      await this.dreamDriftMap.focusProgress(presentation.fromProgress);
    }

    await waitForItPromise(300);

    // Play every Dream Drift-specific opening beat after the popup is visible.
    await this.playOpeningAnimation();
  }

  /** Restore home music when leaving the map, except when entering a puzzle. */
  public override fadeOut(): void {
    // The puzzle sequence owns music while it closes this map to reveal the board.
    if (!this.isStartingPuzzle) sounds.music.play('bgm');

    // Close the map using its standard popup animation.
    super.fadeOut();
  }

  /**
   * Play the complete Dream Drift map opening and restore interaction afterward.
   *
   * @returns Promise resolved after map movement and completion handling settle.
   */
  public async playOpeningAnimation(): Promise<void> {
    // Block map interaction while presenting the latest authoritative progress.
    this.isPresentingProgress = true;
    this.inputBlocker.show();

    // Refresh the transition so timer expiry can reuse this complete sequence.
    const presentation = dreamDriftGetMapPresentation(user(), now());
    this.mapPresentation = presentation;

    // Hold the opened popup briefly before beginning its map story.
    await waitForItPromise(300);

    // Let the player claim settled rewards before changing the map state.
    const presentedRewards = await this.presentRewards();

    // Post-event expired claims close without a lost notice or map animation.
    const stateAfterRewards = user();
    const currentTime = now();
    const claimedExpiredRewards =
      presentedRewards &&
      stateAfterRewards.dreamDrift?.instance?.result === 'expired' &&
      !dreamDriftGetCurrentEvent(stateAfterRewards, currentTime);
    if (claimedExpiredRewards) {
      this.opts.close();
      return;
    }

    // Explain expiry or visible rollback before applying prepared map progress.
    await this.presentTransitionNotice();

    // Present the complete progress or rollback path while scroll follows.
    await this.dreamDriftMap.animateProgress(
      presentation.fromProgress,
      presentation.toProgress,
    );

    // Acknowledge only the exact receipt that was successfully presented.
    if (
      presentation.startedAt !== undefined &&
      presentation.pendingProgress !== undefined
    ) {
      await StateObserver.invoke.dreamDriftAcknowledgeMapTransition({
        startedAt: presentation.startedAt,
        pendingProgress: presentation.pendingProgress,
        progress: presentation.toProgress,
      });
    }

    // Unlock gameplay actions after the map presentation has been consumed.
    this.isPresentingProgress = false;
    this.updatePlayButtonVisibility();
    this.inputBlocker.hide();

    // Only the source event or its reward-claim gap may reopen this recap.
    const result = dreamDriftGetPresentationInstance(user(), now())?.result;

    // Every non-completed map opening finishes through the existing flow unchanged.
    if (result !== 'completed') return;

    // Finish the completed map opening with the Dream Drift recap.
    await openPopupPromise('popupDreamDriftComplete', {});

    // Close the completed map after the player acknowledges the recap.
    this.opts.close();
  }

  /** Load map data and build the static popup before fade-in begins. */
  public async init(args: DreamDriftMapOpenOptions): Promise<void> {
    // Keep the pending claim local to the popup, outside Redux options.
    this.rewardPresentation = undefined;
    const chestType = args.rewardChestType;
    if (chestType) {
      this.rewardPresentation = StateObserver.invoke
        .dreamDriftClaimRewards()
        .then((rewards) => {
          if (rewards.length === 0) return undefined;
          return { chestType, rewards };
        });
    }

    // Load every themed map image before constructing the progress path.
    await this.loadAssets();

    // Read the synchronized player state for map progress and avatar.
    const state = user();
    const currentTime = now();

    // Retrieve the static map description from the selected presentation theme.
    const instance = dreamDriftGetPresentationInstance(state, currentTime);
    const config = this.theme.getMapConfig();

    // Derive the visual transition from the reconciled player state.
    const presentation = dreamDriftGetMapPresentation(state, currentTime);

    // Retain the derived transition for the fade-in opening animation.
    this.mapPresentation = presentation;

    // Prefer the public avatar while retaining the legacy profile fallback.
    const avatar = state.publicProfile?.photo ?? state.profile?.photo ?? '';

    // New events do not inherit the previous event's hero or completion state.
    const storedHero = instance?.hero;
    const hero = dreamDriftHeroes.find(
      (configuredHero) => configuredHero === storedHero,
    );

    // Configure the reusable map component with progress and themed image paths.
    this.dreamDriftMap.setup({
      avatar,
      config,
      finalNode: dreamDriftGetFinalNode(),
      checkpoints: dreamDriftGetCheckpoints(),
      getImagePath: (image) => this.theme.getMapImagePath(image),
      height: this.box.style.height,
      hero,
      progress: presentation.fromProgress,
    });

    // Replace the countdown with a localized terminal label after completion.
    const hasCompletedInstance = instance?.result === 'completed';
    if (hasCompletedInstance) {
      this.timer.setMessage(i18n('match2.races.finished'));
    } else {
      // Start the visible countdown after the popup assets and state are ready.
      this.timer.autoUpdate();
    }

    // Use the standard popup initialization and fade-in lifecycle after setup.
    super.init({});
  }
}
